// Called from tuple wrapper (or itself) to handle simple values inside of a tuple (or handle sub tuples)
import type { FC } from "react"
import React, { useEffect } from "react"
import { useFormik } from "formik"
import { Button, FormLabel, HStack, Input, Stack, Text } from "@chakra-ui/react"

import { encodeSimpleField } from "createProposal/helpers/abi"
import { CREATE_PROPOSAL_ERRORS } from "errors/createProposal"
import CrossMark from "ui/components/icons/CrossMark"

type SimpleArrayValuesProps = {
  changeInputValue: (newValue: any, name?: string) => void
  type: string
  index: number
  removeInputValueAtIndex: (index: number) => void
  value: string
}

type ValuesType = {
  input: string
}

const SimpleArrayValuesInputs: FC<SimpleArrayValuesProps> = ({
  changeInputValue,
  type,
  index,
  removeInputValueAtIndex,
  value,
}) => {
  const initialValues: ValuesType = { input: "" }

  const validate = (values: ValuesType): Record<string, string> => {
    const errors: Record<string, string> = {}

    try {
      encodeSimpleField(type, values.input)

      if (values.input.length < 1) {
        // no value
        errors.input =
          CREATE_PROPOSAL_ERRORS.actions.method.invalidArgs().displayValue
      }
    } catch (error) {
      errors.input =
        CREATE_PROPOSAL_ERRORS.actions.method.invalidArgs().displayValue
    }

    return errors
  }

  const formik = useFormik({
    initialValues: initialValues,
    validate,
    onSubmit: () => {
      // do nothing, values are updating the main form on the go
    },
  })

  useEffect(() => {
    formik.resetForm() // resets when the type changes

    // if formik is added, it's going to keep resetting, we don't want to react to formik changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type])

  useEffect(() => {
    formik.setFieldValue("input", value)

    // if formik is added, it's going to keep resetting, we don't want to react to formik changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])
  const invalid = Boolean(!!formik.errors.input && formik.touched.input)

  const handleBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    if (type !== "string") {
      const trimmedValue = e.target.value.trim()

      await formik.setFieldValue("input", trimmedValue, true)

      changeInputValue(trimmedValue)

      formik.handleBlur(e)

      formik.validateForm()
    }
  }

  return (
    <Stack>
      <HStack h={10}>
        <FormLabel
          alignItems="center"
          display="flex"
          h="full"
          justifyContent="right"
          m={0}
          w={220}
        >
          <Button bg="transparent !important" border={0} mr={0.5} size="sm">
            <CrossMark
              onClick={() => {
                removeInputValueAtIndex(index)
              }}
            />
          </Button>
          <Text
            alignItems="center"
            bg="gray.50"
            border="gray.dark"
            color="purple.600"
            display="flex"
            h="full"
            justifyContent="center"
            w={7}
          >
            {index}
          </Text>
        </FormLabel>
        <Input
          isInvalid={invalid}
          name="input"
          placeholder={type}
          type="text"
          value={value}
          onBlur={handleBlur}
          onChange={(e) => {
            changeInputValue(e.target.value)

            formik.handleChange(e)
          }}
        />
      </HStack>
    </Stack>
  )
}

export default SimpleArrayValuesInputs
